import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import _ from 'lodash'
export * from './migration'
import { RootState } from '../../../store/rootReducer'
import { uuid } from '../../../util/uuid'
import { getProperty } from '../../azure-b2c/storage'
import { ActivePrice, Product } from '../../products/storage'
import { Variant } from '../../products/utils/useVariations'
import { StoreInfo } from '../../stores/storage/stores.types'
import { getUpdatedVariant } from '../utils/getUpdatedVariant'
import {
  CartItem,
  CartPriceErrorResponse,
  CartObject,
  CartOrder,
  CartState,
  SubmitCartApiResponse,
  SyncCartResponse,
  CartDeliveryMode,
} from './cart.types'
import { syncCart, checkoutV2 } from './thunks'
export * from './thunks'

export interface SubmitCartReducerAction {
  data: SubmitCartApiResponse
  store?: StoreInfo
  offlineCartId: string
}

export interface SyncCartReducerAction {
  data: SyncCartResponse
  offlineCartId: string
}

interface TempCartItem extends CartItem {
  tempIndex: number
}

// Items with discount should be sorted to the top
const sortSpecial = (a: TempCartItem | undefined, b: TempCartItem | undefined): number => {
  if (a && b) {
    if (a.itemNumber === b.itemNumber) {
      if (!a.discounts && b.discounts) {
        return 1
      }
      if (a.discounts && !b.discounts) {
        return -1
      }
    }
    return a?.tempIndex - b?.tempIndex
  }
  return 0
}

const removeTempIndex = (tempItem: TempCartItem | undefined): CartItem | undefined => {
  if (tempItem) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { tempIndex, ...item } = tempItem
    return item
  }
  return tempItem
}

const isItem = (item: CartItem | undefined): item is CartItem => {
  return !!item
}

const createInitialCart = (accountNumber: string, store: StoreInfo | undefined): CartObject => {
  return {
    accountNumber: accountNumber,
    offlineCartId: uuid(),
    onlineCartId: null,
    createdAt: new Date(),
    updatedAt: new Date(),
    syncedAt: undefined,
    items: [],
    notes: undefined,
    pickupStore: store,
    totalPrice: 0,
    totalAmountIncGst: 0,
    discountIncGst: 0,
    gstAmount: 0,
    cartDeliveryMode: CartDeliveryMode.PICKUP,
  }
}

export const initialState: CartState = {
  carts: {},
  order: {},
  completeOrders: {},
}

const CartSlice = createSlice({
  name: 'cart',
  initialState: initialState,
  extraReducers: (builder) => {
    builder.addCase(syncCart.pending, (state: CartState, { meta }) => {
      state.order[meta.arg.cart.offlineCartId] = {
        status: 'pending',
        cart: meta.arg.cart,
      }
    })
    builder.addCase(syncCart.fulfilled, (state: CartState, { payload, meta }) => {
      state.order[payload.offlineCartId].status = 'fulfilled'
      const currentCart = state.carts[meta.arg.accountNumber].items
      const copyOfPayload = payload.data.items

      const temp: (TempCartItem | undefined)[] = copyOfPayload.map((syncedItem, index) => {
        const inCartItem = currentCart.find((x) => x.productId === syncedItem.productId)
        if (inCartItem) {
          const cartItem: TempCartItem = {
            ...inCartItem,
            ...syncedItem,
            discounts: syncedItem.discounts,
            basePriceIncGst: syncedItem.basePrice,
            basePriceExcGst: syncedItem.basePriceExcGst,
            discountedPriceExcGst: syncedItem.discountedPriceExcGst,
            discountedPriceIncGst: syncedItem.discountedPriceIncGst,
            tempIndex: index,
          }

          return cartItem
        }
      })

      const sortedTemp = temp.sort(sortSpecial)
      const newCartItems: CartItem[] = sortedTemp.map(removeTempIndex).filter(isItem)

      // Must only trigger if not equal, otherwise will cause an infinite loop.
      if (!_.isEqual(newCartItems, state.carts[meta.arg.accountNumber].items)) {
        state.carts[meta.arg.accountNumber].items = newCartItems
      }

      state.carts[meta.arg.accountNumber].syncedAt = new Date()
      state.carts[meta.arg.accountNumber].totalAmountIncGst = payload.data.totalAmountIncGst
      state.carts[meta.arg.accountNumber].totalPrice = payload.data.totalPrice
      state.carts[meta.arg.accountNumber].gstAmount = payload.data.gstAmount
      state.carts[meta.arg.accountNumber].discountIncGst = payload.data.discountIncGst
      state.carts[meta.arg.accountNumber].onlineCartId = payload.data.cartId
      state.order[payload.offlineCartId].cart = state.carts[meta.arg.accountNumber]
    })
    builder.addCase(syncCart.rejected, (state: CartState, action) => {
      state.order[action.meta.arg.cart.offlineCartId] = {
        error: action.payload,
        cart: action.meta.arg.cart,
        status: 'rejected',
      }
    })

    builder.addCase(checkoutV2.pending, (state: CartState, { meta }) => {
      state.order[meta.arg.cart.offlineCartId] = {
        status: 'pending',
        confirmationId: undefined,
        cart: meta.arg.cart,
      }
    })
    builder.addCase(checkoutV2.fulfilled, (state: CartState, { payload, meta }) => {
      state.order[payload.offlineCartId].confirmationId = payload.data.confirmationId
      state.order[payload.offlineCartId].store = payload.store
      state.order[payload.offlineCartId].status = 'fulfilled'
      const originalCart = state.carts[meta.arg.accountNumber]
      state.carts[meta.arg.accountNumber] = {
        ...createInitialCart(meta.arg.accountNumber, meta.arg.cart.pickupStore),
        lastConfirmationId: payload.data.confirmationId,
        deliveryDetails: originalCart.deliveryDetails,
        cartDeliveryMode: originalCart.cartDeliveryMode,
        pickupStore: originalCart.pickupStore,
      }
      state.completeOrders[meta.arg.accountNumber] = {
        ...state.completeOrders[meta.arg.accountNumber],
        [payload.data.confirmationId]: {
          ...state.order[payload.offlineCartId],
          confirmationId: payload.data.confirmationId,
        },
      }
    })
    builder.addCase(checkoutV2.rejected, (state: CartState, action) => {
      state.order[action.meta.arg.cart.offlineCartId].error = action.payload
      state.order[action.meta.arg.cart.offlineCartId].cart = action.meta.arg.cart
      state.order[action.meta.arg.cart.offlineCartId].status = 'rejected'
    })
  },
  reducers: {
    updateVariant(
      state: CartState,
      {
        payload: { accountNumber, item, variant },
      }: PayloadAction<{ accountNumber: string; item: Product; variant: Variant }>,
    ) {
      const index = state.carts[accountNumber].items.findIndex(
        (p) => p.productId === item.productId,
      )
      state.carts[accountNumber].items[index] = getUpdatedVariant(
        variant,
        state.carts[accountNumber].items[index],
      )
    },
    clearOrderStatus(
      state: CartState,
      action: PayloadAction<{ accountNumber: string; cart?: CartObject }>,
    ) {
      if (action.payload.cart) {
        delete state.order[action.payload.cart?.offlineCartId]
        state.order[action.payload.cart?.offlineCartId] = {
          cart: action.payload.cart,
          status: 'idle',
        }
      }
    },
    clearConfirmationId(state: CartState, action: PayloadAction<{ accountNumber: string }>) {
      state.carts[action.payload.accountNumber].lastConfirmationId = undefined
    },
    removeItem(
      state: CartState,
      {
        payload: { accountNumber, product },
      }: PayloadAction<{ accountNumber: string; product: CartItem }>,
    ) {
      state.carts[accountNumber].items = state.carts[accountNumber].items.filter(
        (a) => !_.isEqual(a, product),
      )

      state.carts[accountNumber].updatedAt = new Date()
    },

    removeItems(
      state: CartState,
      {
        payload: { accountNumber, products },
      }: PayloadAction<{ accountNumber: string; products: number[] }>,
    ) {
      products.forEach((product) => {
        state.carts[accountNumber].items = state.carts[accountNumber].items.filter(
          (a) => a.productId !== product,
        )
      })
      state.carts[accountNumber].updatedAt = new Date()
    },

    updateItemsPrice(
      state: CartState,
      {
        payload: { accountNumber, products },
      }: PayloadAction<{ accountNumber: string; products: CartPriceErrorResponse[] }>,
    ) {
      products.forEach((product) => {
        const item = state.carts[accountNumber].items.find((a) => a.productId === product.productId)
        if (item) {
          item.basePriceExcGst = product.newPrice ?? 0
        }
      })
      state.carts[accountNumber].updatedAt = new Date()
    },

    setQuantity(
      state: CartState,
      {
        payload: { accountNumber, product, amount, isOnline },
      }: PayloadAction<{
        accountNumber: string
        product: CartItem
        amount: number
        isOnline?: boolean
      }>,
    ) {
      let item
      if (product.lineId) {
        item = state.carts[accountNumber].items.find((a) => a.lineId === product.lineId)
      } else {
        item = state.carts[accountNumber].items.find((a) => a.productId === product.productId)
      }
      if (item) {
        // Remove negative or 0 quantities from cart
        if (item.quantity <= 0) {
          state.carts[accountNumber].items = state.carts[accountNumber].items.filter(
            (a) => a.productId !== product.productId,
          )
        } else {
          if (!isOnline) {
            const discountedPriceExcGst = item.discountedPriceExcGst / item.quantity
            const discountedPriceIncGst = item.discountedPriceIncGst / item.quantity
            const basePriceExcGst = item.basePriceExcGst / item.quantity
            const basePriceIncGst = item.basePriceIncGst / item.quantity

            item.discountedPriceExcGst = discountedPriceExcGst * amount
            item.discountedPriceIncGst = discountedPriceIncGst * amount
            item.basePriceExcGst = basePriceExcGst * amount
            item.basePriceIncGst = basePriceIncGst * amount
          }
          item.quantity = amount
        }
      }

      const { items } = state.carts[accountNumber]
      let totalAmountIncGst = 0
      let gstAmount = 0
      let discountIncGst = 0

      items.forEach((item) => {
        totalAmountIncGst += item.discountedPriceIncGst
        gstAmount += item.discountedPriceIncGst - item.discountedPriceExcGst
        discountIncGst += item.basePriceIncGst - item.discountedPriceIncGst
      })

      if (!isOnline) {
        state.carts[accountNumber].totalAmountIncGst = totalAmountIncGst
        state.carts[accountNumber].totalPrice = totalAmountIncGst
        state.carts[accountNumber].gstAmount = gstAmount
        state.carts[accountNumber].discountIncGst = discountIncGst
      }

      state.carts[accountNumber].updatedAt = new Date()
    },

    setEditedPrice(
      state: CartState,
      {
        payload: { accountNumber, product, editedPrice, editedQuantity, activePrice },
      }: PayloadAction<{
        accountNumber: string
        product: CartItem
        editedPrice?: number
        editedQuantity: number
        activePrice?: ActivePrice
      }>,
    ) {
      let item
      if (product.lineId) {
        item = state.carts[accountNumber].items.find((a) => a.lineId === product.lineId)
      } else {
        item = state.carts[accountNumber].items.find((a) => a.productId === product.productId)
      }

      if (item) {
        item.editedPriceExcGst = editedPrice
        item.quantity = editedQuantity

        if (activePrice) {
          item.basePriceExcGst = activePrice.basePriceExcGst
          item.discountedPriceExcGst = activePrice.discountedPriceExcGst
          item.discountedPriceIncGst = activePrice.discountedPriceIncGst
        }
      }

      state.carts[accountNumber].updatedAt = new Date()
    },

    setStockAvailability(
      state: CartState,
      {
        payload: { accountNumber, productWithStockAvailabilities },
      }: PayloadAction<{
        accountNumber: string
        productWithStockAvailabilities: CartItem[]
      }>,
    ) {
      const items = state.carts[accountNumber].items

      productWithStockAvailabilities.forEach((product) => {
        const item = items.find((a) => a.productId === product.productId)
        if (item) {
          item.stockAvailabilities = product.stockAvailabilities
        }
      })
      state.carts[accountNumber].updatedAt = new Date()
    },

    clearCart(state: CartState, action: PayloadAction<{ accountNumber: string }>) {
      delete state.carts[action.payload.accountNumber]
    },

    setDeliveryMode(
      state: CartState,
      action: PayloadAction<{
        accountNumber: string
        cartDeliveryMode: CartDeliveryMode
      }>,
    ) {
      const { accountNumber, cartDeliveryMode } = action.payload

      if (!Object.keys(state.carts).includes(accountNumber)) {
        state.carts[accountNumber] = createInitialCart(accountNumber, undefined)
      }

      state.carts[accountNumber].cartDeliveryMode = cartDeliveryMode
      state.carts[accountNumber].updatedAt = new Date()
    },

    setUrgent(
      state: CartState,
      action: PayloadAction<{
        accountNumber: string
        urgent: boolean
      }>,
    ) {
      const { accountNumber, urgent } = action.payload

      if (!Object.keys(state.carts).includes(accountNumber)) {
        state.carts[accountNumber] = createInitialCart(accountNumber, undefined)
      }

      state.carts[accountNumber].deliveryDetails = {
        ...state.carts[accountNumber].deliveryDetails,
        urgent: urgent,
      }
    },

    setDeliveryDetails(
      state: CartState,
      action: PayloadAction<{
        accountNumber: string
        delivery: Pick<CartObject, 'deliveryDetails'>
      }>,
    ) {
      const {
        accountNumber,
        delivery: { deliveryDetails },
      } = action.payload

      if (!Object.keys(state.carts).includes(accountNumber)) {
        state.carts[accountNumber] = createInitialCart(accountNumber, undefined)
      }

      state.carts[accountNumber].cartDeliveryMode = CartDeliveryMode.DELIVERY
      state.carts[accountNumber].updatedAt = new Date()
      state.carts[accountNumber].deliveryDetails = deliveryDetails
    },

    setPickUpStore(
      state: CartState,
      action: PayloadAction<{ accountNumber: string; pickupStore: StoreInfo | undefined }>,
    ) {
      const { accountNumber, pickupStore } = action.payload

      if (!Object.keys(state.carts).includes(accountNumber)) {
        state.carts[accountNumber] = createInitialCart(accountNumber, undefined)
      }

      state.carts[accountNumber].pickupStore = pickupStore
      state.carts[accountNumber].updatedAt = new Date()
    },

    saveNotes(
      state: CartState,
      action: PayloadAction<{ accountNumber: string; notes: string | undefined }>,
    ) {
      const { accountNumber, notes } = action.payload
      if (Object.keys(state.carts).includes(accountNumber)) {
        state.carts[accountNumber].notes = notes
      }
      state.carts[accountNumber].updatedAt = new Date()
    },

    saveInternalNotes(
      state: CartState,
      action: PayloadAction<{ accountNumber: string; internalNotes: string | undefined }>,
    ) {
      const { accountNumber, internalNotes } = action.payload
      if (Object.keys(state.carts).includes(accountNumber)) {
        state.carts[accountNumber].internalNotes = internalNotes
      }
      state.carts[accountNumber].updatedAt = new Date()
    },

    saveCustomerReference(
      state: CartState,
      action: PayloadAction<{ accountNumber: string; customerReference: string | undefined }>,
    ) {
      const { accountNumber, customerReference } = action.payload
      if (Object.keys(state.carts).includes(accountNumber)) {
        state.carts[accountNumber].customerReference = customerReference
      }
      state.carts[action.payload.accountNumber].updatedAt = new Date()
    },

    addToCart(
      state: CartState,
      action: PayloadAction<{
        accountNumber: string
        product: CartItem
      }>,
    ) {
      const { accountNumber, product } = action.payload

      if (!Object.keys(state.carts).includes(accountNumber)) {
        state.carts[accountNumber] = createInitialCart(accountNumber, undefined)
      }

      const item = state.carts[action.payload.accountNumber].items.find(
        (a) => a.productId === product.productId,
      )

      if (item) {
        const discountedPriceExcGst = item.discountedPriceExcGst / item.quantity
        const discountedPriceIncGst = item.discountedPriceIncGst / item.quantity
        const basePriceExcGst = item.basePriceExcGst / item.quantity
        const basePriceIncGst = item.basePriceIncGst / item.quantity

        item.quantity += product.quantity
        item.discountedPriceExcGst = discountedPriceExcGst * item.quantity
        item.discountedPriceIncGst = discountedPriceIncGst * item.quantity
        item.basePriceExcGst = basePriceExcGst * item.quantity
        item.basePriceIncGst = basePriceIncGst * item.quantity
      } else {
        state.carts[action.payload.accountNumber].items.push({
          ...product,
          discountedPriceExcGst: product.discountedPriceExcGst * product.quantity,
          discountedPriceIncGst: product.discountedPriceIncGst * product.quantity,
          basePriceExcGst: product.basePriceExcGst * product.quantity,
          basePriceIncGst: product.basePriceIncGst * product.quantity,
        })
      }
      state.carts[action.payload.accountNumber].updatedAt = new Date()
    },
  },
})

const selectState = (state: RootState) => state
export const selectCurrentCart = createSelector(
  selectState,
  (state): CartObject | undefined => state.cart.carts[getProperty(state) as string] ?? undefined,
)

export const selectOrderStatus = createSelector(
  selectState,
  (state): CartObject | undefined => state.cart.carts[getProperty(state) as string] ?? undefined,
)

export const selectCartDeliveryMode = createSelector(
  selectState,
  (state): CartDeliveryMode | undefined => {
    if (state.cart.carts[getProperty(state) as string]) {
      return state.cart.carts[getProperty(state) as string].cartDeliveryMode
    }
    return undefined
  },
)

export const selectOrderStatusById = (id: string | undefined) => {
  return createSelector(selectState, (state): CartOrder | undefined =>
    id ? state.cart.order[id] ?? undefined : undefined,
  )
}

export const selectOrderConfirmationById = (id: string | undefined) => {
  return createSelector(selectState, (state) => {
    if (id && state.cart.completeOrders[getProperty(state) as string]) {
      return state.cart.completeOrders[getProperty(state) as string][id]
    }

    return undefined
  })
}

export type { CartState } from './cart.types'
export default CartSlice
